import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { CommissionEntry, CommissionLedger, Marketer } from "../lib/types";
import {
  Button,
  Card,
  CommissionStatusBadge,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
  formatMoney,
} from "../components/ui";

const STATUSES = ["pending", "approved", "paid", "void"] as const;

export function Commission() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const queryClient = useQueryClient();

  const [status, setStatus] = useState("");
  const [marketerId, setMarketerId] = useState("");
  const [paying, setPaying] = useState<CommissionEntry | null>(null);

  const params = new URLSearchParams();
  if (status.length > 0) params.set("status", status);
  if (isAdmin && marketerId.length > 0) params.set("marketerId", marketerId);

  const ledger = useQuery({
    queryKey: ["commission", params.toString()],
    queryFn: () => api<CommissionLedger>(`/api/v1/admin/commission?${params.toString()}`),
  });

  const marketers = useQuery({
    queryKey: ["marketers"],
    queryFn: () => api<Marketer[]>("/api/v1/admin/marketers"),
    enabled: isAdmin,
  });

  const transition = useMutation({
    mutationFn: (input: { id: string; status: string; reference?: string }) =>
      api(`/api/v1/admin/commission/${input.id}/status`, {
        method: "POST",
        body: {
          status: input.status,
          ...(input.reference === undefined ? {} : { reference: input.reference }),
        },
      }),
    onSuccess: () => {
      setPaying(null);
      void queryClient.invalidateQueries({ queryKey: ["commission"] });
      void queryClient.invalidateQueries({ queryKey: ["order"] });
    },
  });

  const totals = ledger.data?.totals;

  return (
    <div className="space-y-6">
      <PageHeader
        title={isAdmin ? "Commission" : "Your commission"}
        lede={
          isAdmin
            ? "What each sale earned, and what has actually been paid out."
            : "What you have earned. Payments are sent separately and recorded here."
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Total label="Owed" hint="Earned, not yet paid" cents={totals?.owedCents} emphasis />
        <Total label="Pending" hint="Not yet approved" cents={totals?.pendingCents} />
        <Total label="Approved" hint="Cleared to pay" cents={totals?.approvedCents} />
        <Total label="Paid" hint="Settled to date" cents={totals?.paidCents} />
      </div>

      <div className="flex flex-wrap gap-3">
        {/* Full width until there is room for two, so neither select is forced
            past the edge of a phone. */}
        <div className="w-full sm:w-44">
          <Field label="Status">
            <Select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {isAdmin && (
          <div className="w-full sm:w-56">
            <Field label="Marketer">
              <Select value={marketerId} onChange={(event) => setMarketerId(event.target.value)}>
                <option value="">Everyone</option>
                {(marketers.data ?? []).map((marketer) => (
                  <option key={marketer.id} value={marketer.id}>
                    {marketer.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}
      </div>

      {paying !== null && (
        <PayoutForm
          entry={paying}
          pending={transition.isPending}
          error={transition.isError ? (transition.error as Error).message : null}
          onCancel={() => setPaying(null)}
          onSubmit={(reference) =>
            transition.mutate({ id: paying.id, status: "paid", reference })
          }
        />
      )}

      {transition.isError && paying === null && (
        <ErrorNote>{(transition.error as Error).message}</ErrorNote>
      )}

      <Card>
        {ledger.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : ledger.isError ? (
          <div className="p-4">
            <ErrorNote>Could not load the ledger. Reload to try again.</ErrorNote>
          </div>
        ) : ledger.data.items.length === 0 ? (
          <EmptyState>
            {status.length > 0 || marketerId.length > 0
              ? "Nothing matches those filters."
              : "No commission yet. An entry appears when an attributed order is paid."}
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Order</Th>
                  {isAdmin && <Th>Marketer</Th>}
                  <Th>Customer</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Reference</Th>
                  <Th>Earned</Th>
                  {isAdmin && <Th>Settle</Th>}
                </tr>
              </thead>
              <tbody>
                {ledger.data.items.map((entry) => {
                  const settleable = entry.status === "pending" || entry.status === "approved";

                  return (
                    <tr key={entry.id} className="border-b border-hairline last:border-0">
                      <Td label="Order">
                        <Link
                          to={`/orders/${entry.order.id}`}
                          className="tabular text-accent underline-offset-4 hover:underline"
                        >
                          {entry.order.number}
                        </Link>
                        <span className="block text-xs text-dim">{entry.order.product.name}</span>
                      </Td>
                      {isAdmin && <Td label="Marketer">{entry.marketer.name}</Td>}
                      <Td label="Customer" className="text-muted">
                        {entry.order.customer.name}
                      </Td>
                      <Td label="Amount" className="tabular">
                        {formatMoney(entry.amountCents, entry.currency)}
                      </Td>
                      <Td label="Status">
                        <CommissionStatusBadge status={entry.status} />
                      </Td>
                      <Td label="Reference" className="text-muted">
                        {entry.reference ?? <span className="text-dim">—</span>}
                      </Td>
                      <Td label="Earned" className="text-muted">
                        {formatDate(entry.order.paidAt)}
                      </Td>
                      {isAdmin && (
                        /* A paid or void entry has nothing left to settle. The
                           cell is left genuinely empty so the card drops it
                           rather than showing a heading with no action under
                           it. */
                        <Td {...(settleable ? { label: "Settle" } : {})}>
                          {settleable && (
                            <div className="flex flex-wrap gap-1">
                              {entry.status === "pending" && (
                                <Button
                                  disabled={transition.isPending}
                                  onClick={() =>
                                    transition.mutate({ id: entry.id, status: "approved" })
                                  }
                                >
                                  Approve
                                </Button>
                              )}
                              {entry.status === "approved" && (
                                <Button variant="primary" onClick={() => setPaying(entry)}>
                                  Mark paid
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                disabled={transition.isPending}
                                onClick={() => transition.mutate({ id: entry.id, status: "void" })}
                              >
                                Void
                              </Button>
                            </div>
                          )}
                        </Td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}

function Total({
  label,
  hint,
  cents,
  emphasis = false,
}: {
  label: string;
  hint: string;
  cents: number | undefined;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border border-hairline bg-surface px-4 py-3">
      <p className="field-label">{label}</p>
      <p className={`tabular text-2xl ${emphasis ? "text-accent" : "text-ink"}`}>
        {cents === undefined ? "—" : formatMoney(cents, "USD")}
      </p>
      <p className="text-xs text-dim">{hint}</p>
    </div>
  );
}

/**
 * The reference is required by the server, and for good reason: this system
 * never moves the money, so the row is the only record of how it moved.
 */
function PayoutForm({
  entry,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  entry: CommissionEntry;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (reference: string) => void;
}) {
  const [reference, setReference] = useState("");

  return (
    <Card title={`Record payment to ${entry.marketer.name}`}>
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(reference.trim());
        }}
      >
        <p className="text-sm text-muted">
          Marking {formatMoney(entry.amountCents, entry.currency)} on {entry.order.number} as paid.
          This records that you sent it — it does not send anything.
        </p>

        <Field label="How it was paid" hint="Bank reference, transfer id, cheque number — anything that finds it again.">
          <Input
            value={reference}
            required
            autoFocus
            placeholder="Bank transfer 8829104"
            onChange={(event) => setReference(event.target.value)}
          />
        </Field>

        {error !== null && <ErrorNote>{error}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={pending || reference.trim().length === 0}>
            {pending ? "Recording…" : "Mark as paid"}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
